import { CommonModule } from '@angular/common';
import { Component, effect, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { CdkDragEnd, DragDropModule } from '@angular/cdk/drag-drop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import { AriadneContextService } from 'src/app/core/ariadne-context.service';

/**
 * AriadneChatFrame — the DECOUPLED chat: a thin floating shell hosting AriadneXO's own chat UI in an
 * <iframe> (served by Ariadne at /chat). This replaces the in-app Angular chat (app-ariadne-chat) so
 * the chat evolves in Ariadne alone; the orchestrator only feeds context over an origin-checked
 * postMessage bridge and follows navigation requests. Auth rides Ariadne's own SSO cookie (the iframe
 * is Ariadne's origin, so /api/ariadne/* is same-origin there — same trust the old chat used with
 * withCredentials).
 *
 * Contract (mirror of Ariadne's EmbeddedChat):
 *   host -> chat:  {type:'ariadne:init', theme} · {type:'ariadne:context', entity, rithmId}
 *   chat -> host:  {type:'ariadne:ready'} · {type:'ariadne:navigate', deepLink} · {type:'ariadne:resize', height}
 */
@Component({
  selector: 'app-ariadne-chat-frame',
  standalone: true,
  imports: [CommonModule, MatIconModule, DragDropModule],
  templateUrl: './ariadne-chat-frame.component.html',
  styleUrls: ['./ariadne-chat-frame.component.scss'],
})
export class AriadneChatFrameComponent {
  /** Whether the floating panel is open. */
  readonly isOpen = signal(false);

  /** Free-drag offset of the bubble from its bottom-right anchor, persisted across reloads so the
   * user's chosen spot sticks (the old in-app chat was draggable — keep that). */
  readonly dragPosition = signal(this.loadDragPos());

  private loadDragPos(): { x: number; y: number } {
    try {
      const raw = localStorage.getItem('ariadne.fab.pos');
      if (raw) {
        const p = JSON.parse(raw);
        if (typeof p?.x === 'number' && typeof p?.y === 'number') return { x: p.x, y: p.y };
      }
    } catch { /* ignore */ }
    return { x: 0, y: 0 };
  }

  onDragEnded(e: CdkDragEnd): void {
    const p = e.source.getFreeDragPosition();
    this.dragPosition.set({ x: p.x, y: p.y });
    try { localStorage.setItem('ariadne.fab.pos', JSON.stringify({ x: p.x, y: p.y })); } catch { /* ignore */ }
  }

  /** Ariadne base URL (same source the old chat used). Empty when unconfigured → the shell hides. */
  private readonly baseUrl = (environment as { ariadneApiUrl?: string }).ariadneApiUrl ?? '';

  /** Origin allowed to postMessage us (and the only target we post to). */
  private readonly ariadneOrigin = this.baseUrl ? new URL(this.baseUrl).origin : '';

  /** Sanitized iframe src = Ariadne's embeddable chat route. */
  readonly chatUrl: SafeResourceUrl;

  /** The iframe's window, captured on its 'ready' handshake — the target for every host→chat post. */
  private frame: Window | null = null;

  get configured(): boolean {
    return !!this.baseUrl;
  }

  constructor(
    private sanitizer: DomSanitizer,
    private router: Router,
    private contextService: AriadneContextService,
  ) {
    this.chatUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`${this.baseUrl}/chat`);

    window.addEventListener('message', this.onMessage);

    // Push context to the chat whenever "where the user is" changes (once the frame is ready).
    effect(() => {
      // read the signal so the effect re-runs on change
      this.contextService.context();
      this.sendContext();
    });
  }

  onBubbleClick(): void {
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
  }

  /** Host <- chat messages. Origin-fenced to the Ariadne origin. */
  private onMessage = (e: MessageEvent): void => {
    if (!this.ariadneOrigin || e.origin !== this.ariadneOrigin) return;
    const data = e.data as { type?: string; deepLink?: string };
    if (!data || typeof data.type !== 'string' || !data.type.startsWith('ariadne:')) return;
    switch (data.type) {
      case 'ariadne:ready':
        this.frame = e.source as Window;
        this.sendInit();
        this.sendContext();
        break;
      case 'ariadne:navigate':
        if (data.deepLink) this.router.navigateByUrl(data.deepLink);
        break;
      // 'ariadne:resize' — the panel is fixed-size here, so height is ignored for now.
    }
  };

  private post(msg: Record<string, unknown>): void {
    if (!this.frame || !this.ariadneOrigin) return;
    this.frame.postMessage(msg, this.ariadneOrigin);
  }

  private sendInit(): void {
    const theme = window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    this.post({ type: 'ariadne:init', theme });
  }

  /** Sends the full live context (route + area + entity-in-view) so the chat grounds itself exactly
   * like the old in-app assistant did. The chat maps entity.type → its station/container token. */
  private sendContext(): void {
    if (!this.frame) return;
    this.post({ type: 'ariadne:context', context: this.contextService.context() });
  }
}
